// src/pages/tournament/MatchupCard.jsx
import React from 'react';
import { useConfirm } from '../../components/ConfirmDialog';
import { PlusIcon, TrashIcon } from '../../components/icons';
import SetRow from './SetRow';
import { setsWon } from './roundsState';
import { groupName } from '../../utils/groups';

function Team({ label, className, team }) {
    const players = team?.players || [];
    return (
        <div className={`team ${className}`}>
            <div className="team-label">{label}</div>
            {players.length > 0 ? (
                <ul>
                    {players.map((player) => (
                        <li key={player.playerId}>
                            {player.firstName} {player.lastName}
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="hint">No players</p>
            )}
        </div>
    );
}

function MatchCard({ match, matchupId, roundId, actions, locked }) {
    const confirm = useConfirm();
    const sets = match.sets || [];
    const won = setsWon(match);
    const played = won.team1 + won.team2 > 0;
    const number = match.matchId + 1;

    const handleDeleteMatch = async () => {
        const confirmed = await confirm({
            title: `Delete match ${number}?`,
            message: 'Its sets and scores will be removed.',
            confirmLabel: 'Delete match',
        });
        if (confirmed) actions.deleteMatch(roundId, matchupId, match.matchId);
    };

    const handleDeleteSet = async (set, setNumber) => {
        const confirmed = await confirm({
            title: `Delete set ${setNumber}?`,
            message: 'Its score will be removed.',
            confirmLabel: 'Delete set',
        });
        if (confirmed) actions.deleteSet(roundId, matchupId, match.matchId, set.setId);
    };

    return (
        <div className="match">
            <div className="match-head">
                <div className="match-title">
                    Match {number}
                </div>
                {played && (
                        <span className="match-score" title="Sets won">
<span className={`ro-score team-a${won.team1 > won.team2 ? ' leading' : ''}`}>{won.team1}</span>
<span className="score-dash" aria-hidden="true">{' – '}</span>
<span className={`ro-score team-b${won.team2 > won.team1 ? ' leading' : ''}`}>{won.team2}</span>
</span>
                )}
                {!locked && (
                    <div className="match-actions">
                        <button
                            className="btn btn-sm"
                            onClick={() => actions.addSet(roundId, matchupId, match.matchId)}
                        >
                            <PlusIcon size={14} /> Add set
                        </button>
                        <button
                            className="btn btn-ghost btn-icon btn-sm btn-icon-danger"
                            aria-label={`Delete match ${number}`}
                            onClick={handleDeleteMatch}
                        >
                            <TrashIcon size={16} />
                        </button>
                    </div>
                )}
            </div>
            {sets.length === 0 ? (
                <p className="hint">No sets yet. Add a set to start scoring.</p>
            ) : (
                sets.map((set, index) => (
                    <SetRow
                        key={set.setId}
                        setItem={set}
                        number={index + 1}
                        readOnly={locked}
                        onSave={(team1, team2) =>
                            actions.saveScore(roundId, matchupId, match.matchId, set.setId, team1, team2)
                        }
                        onDelete={() => handleDeleteSet(set, index + 1)}
                    />
                ))
            )}
        </div>
    );
}

// canCancel: only the latest round can have matchups cancelled or reinstated; once a newer round is planned,
// the pairings of the older ones are history (their break counts fed into the next round).
function MatchupCard({ matchup, index, courtNumber, roundId, actions, locked, canCancel = true }) {
    const confirm = useConfirm();
    const matches = matchup.matches || [];
    const cancelled = Boolean(matchup.cancelled);
    const readOnly = locked || cancelled;

    const handleCancel = async () => {
        const confirmed = await confirm({
            title: `Cancel matchup ${index + 1}?`,
            message: 'It will count as a break for its players instead of a game. You can reinstate it until the next round is planned.',
            confirmLabel: 'Cancel matchup',
            danger: false,
        });
        if (confirmed) actions.cancelMatchup(roundId, matchup.matchupId);
    };

    return (
        <section className={`matchup${cancelled ? ' cancelled' : ''}`} aria-label={`Matchup ${index + 1}`}>
            <div className="matchup-head">
                <span>
                    Matchup {index + 1}
                    {matchup.group != null && <span className="badge">Group {groupName(matchup.group)}</span>}
                    {matchup.label && <span className="badge badge-primary">{matchup.label}</span>}
                    {matchup.isSingles && <span className="badge badge-primary">Singles</span>}
                    {matchup.requested && <span className="badge badge-primary" title="The players asked for this pairing">Requested</span>}
                    {courtNumber && <span className="badge">Court {courtNumber}</span>}
                    {cancelled && <span className="badge badge-cancelled">Cancelled</span>}
                </span>
                {!locked && (
                    <div className="matchup-head-actions">
                        {cancelled ? (
                            canCancel && (
                                <button className="btn btn-sm" onClick={() => actions.reinstateMatchup(roundId, matchup.matchupId)}>
                                    Reinstate
                                </button>
                            )
                        ) : (
                            <>
                                <button className="btn btn-sm" onClick={() => actions.addMatch(roundId, matchup.matchupId)}>
                                    <PlusIcon size={14} /> Add match
                                </button>
                                {canCancel && (
                                    <button className="btn btn-ghost btn-sm" onClick={handleCancel}>
                                        Cancel matchup
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
            <div className="teams">
                <Team label={matchup.isSingles ? 'Player A' : 'Team A'} className="team-a" team={matchup.team1} />
                <span className="vs">VS</span>
                <Team label={matchup.isSingles ? 'Player B' : 'Team B'} className="team-b" team={matchup.team2} />
            </div>
            <div className="matches">
                {matches.length === 0 ? (
                    <p className="hint">No matches yet.</p>
                ) : (
                    matches.map((match) => (
                        <MatchCard
                            key={match.matchId}
                            match={match}
                            matchupId={matchup.matchupId}
                            roundId={roundId}
                            actions={actions}
                            locked={readOnly}
                        />
                    ))
                )}
            </div>
        </section>
    );
}

export default MatchupCard;
